<template>
  <b-modal
      :title="title"
      hide-footer
      v-model="localVisible"
      @hide="closeModal"
  >
    <b-input-group class="input-group-merge">
      <b-form-input
          id="refund_in_percentage"
          required
          v-model="dataToPrint.valueToModify"
          @input="changeValueToModify"
      ></b-form-input>
    </b-input-group>

    <p
        class="p-2 mt-1 b-radius-lg bg-light text-dark"
        v-if="dataToPrint.differenceValue"
    >
      {{ firstTitle }}:
      <b>{{ amountFormat(dataToPrint.differenceValue, invoiceClone.currency.name) }}</b>
    </p>

    <p
        class="p-2 mt-1 b-radius-lg bg-light text-dark"
        v-if="dataToPrint.absoluteAmountToPrint"
    >
      {{ secondTitle }}:
      <b>{{ amountFormat(dataToPrint.absoluteAmountToPrint, invoiceClone.currency.name) }}</b>
    </p>

    <b-form-checkbox
        class="mt-1"
        switch
        v-model="save_new_data_for_future_invoices"
        variant="success"
    >
      {{ $t('save_percentage_future_invoices') }}
    </b-form-checkbox>

    <b-button
        class="btn mt-1"
        variant="primary"
        @click="submitChange"
    >
      {{ $t('change') }}
    </b-button>
  </b-modal>
</template>

<script>
import CalculationsHelper from "@/helpers/calculationsHelper";

export default {
  props: {
    isVisible: Boolean,
    invoice: Object,
    amountFormat: Function,
    calculateRefundIfDefaults: Function,
    CalculateEffectiveValue: Function,
    type: String
  },
  data() {
    return {
      dataToPrint: {
        valueToModify: 0,
        differenceValue: 0,
        absoluteAmountToPrint: 0,
      },
      localVisible: this.isVisible,
      CalculationsHelper,
      invoiceClone: structuredClone(this.invoice),
      save_new_data_for_future_invoices: false
    }
  },
  created() {
    this.initRefundData();
  },
  computed: {
    title(){
      return this.switchCase(this.$t('refund_if_defaults_short'), this.$t('refund_percent'), this.$t('investor-fee'), this.$t("fee_hive_new"));
    },
    firstTitle(){
      return this.switchCase(this.$t('effective_value'), this.$t('effective_value'), this.$t('roi'),
          this.invoiceClone.type === 'reverse' ? this.$t('effective_value') : this.$t('roi'));
    },
    secondTitle(){
      return this.switchCase(this.$t('refund_amount'), this.$t('refund_amount'), this.$t('investor-fee'), this.$t("fee_hive_new"));
    },
    submitChange() {
      return this.switchCase(this.changeRefund, this.changeRefund, this.changeInvestorRate, this.changeInvestorRate);
    },
  },
  watch: {
    isVisible(val) { this.localVisible = val },
    type() { this.initRefundData() }
  },
  methods: {
    switchCase(){
      switch (this.type) {
        case 'refund_default':
          return arguments[0];
        case 'refund':
          return arguments[1];
        case 'aequitex-fee-investor':
          return arguments[2];
        case 'aequitex-fee-seller':
          return arguments[3];
        default:
          throw new Error('Wrong action type');
      }
    },

    sanitizeNumber(v) {
      return Number(String(v).replace(/[^0-9.-]+/g, '')) || 0;
    },

    recalc() {
      const scoring = this.invoiceClone?.scoring || {};
      const amount = this.sanitizeNumber(scoring.amount ?? this.invoiceClone?.amount);

      const percentage = Number(this.dataToPrint.valueToModify) || 0;
      this.dataToPrint.absoluteAmountToPrint = amount * (percentage / 100);

      const scoringClone = { ...scoring };
      if (this.type === 'refund') scoringClone.refund_percentage = percentage;
      else if(this.type === 'refund_default') scoringClone.refund_if_defaults = percentage;
      else if(this.type === 'aequitex-fee-seller' && this.invoiceClone.type === 'reverse') scoringClone.fee_hive = this.dataToPrint.valueToModify;
      this.invoiceClone.scoring = scoringClone;
      const refundEffectiveValue = this.CalculationsHelper.getLiquidityAmount(this.invoiceClone) || 0;
      const realRoi = this.calculateRoiAmount();
      this.dataToPrint.differenceValue = this.switchCase(
          refundEffectiveValue,
          refundEffectiveValue,
          realRoi,
          this.invoiceClone.type === 'reverse' ? refundEffectiveValue : realRoi
      )
    },

    calculateRoiAmount() {
      if (!this.invoiceClone || !this.invoiceClone.scoring) return 0;

      switch (this.type) {
        case 'aequitex-fee-investor':
          this.invoiceClone.scoring.investor_fee = this.dataToPrint.valueToModify;
          return this.CalculationsHelper.getRoiAmount(this.invoiceClone);
        case 'aequitex-fee-seller':
          this.invoiceClone.scoring.fee_hive = this.dataToPrint.valueToModify;
          return this.CalculationsHelper.getRoiAmount(this.invoiceClone);
        default:
          return undefined;
      }
    },

    changeValueToModify() {
      this.recalc();
    },

    initRefundData() {
      if (!this.invoiceClone) return;

      const scoring = this.invoiceClone.scoring || {};
      if (this.type === 'refund') {
        this.dataToPrint.valueToModify = scoring.refund_percentage ?? 0;
      } else if(this.type === 'refund_default') {
        this.dataToPrint.valueToModify = scoring.refund_if_defaults ?? 0;
      } else if(this.type === 'aequitex-fee-investor'){
        this.dataToPrint.valueToModify = Number(this.invoiceClone?.scoring?.investor_fee ?? 0).toFixed(2);
      } else if(this.type === 'aequitex-fee-seller'){
        this.dataToPrint.valueToModify = Number(this.invoiceClone?.scoring?.fee_hive ?? 0).toFixed(2);
      }

      this.recalc();
    },

    changeRefund(isChangingRefund = true) {
      if (!this.invoiceClone) return;

      const percentage = Number(this.dataToPrint.valueToModify) || 0;
      const payload = this.type === 'refund'
          ? { refund_percentage: percentage }
          : { refund_if_defaults: percentage };

      const amount = this.sanitizeNumber(this.invoiceClone?.scoring?.amount ?? this.invoiceClone?.amount);
      const body = isChangingRefund
          ? {
            ...payload,
            differenceValue: this.CalculateNewRefund
                ? this.CalculateNewRefund(amount, this.dataToPrint.absoluteAmountToPrint)
                : this.dataToPrint.differenceValue,
            saveForFuture: this.save_new_data_for_future_invoices,
            refundType: this.type,
          }
          : {};

      this.$http.post(`/invoice/admin/change_refund/${this.invoiceClone.id}`, body)
          .then(res => {
            if (res.data.status === 200) {
              this.showSuccessMessage(res.data.message);
              setTimeout(() => window.location.reload(), 1000);
            }
          })
          .catch(() => {
            this.$swal({ icon: 'error', title: this.$t('error_occurred') });
          });
    },

    changeInvestorRate() {
      const feeAmountToSend = this.sanitizeNumber(this.dataToPrint.valueToModify);

      this.$http.post(`/invoice/admin/change_fees/${this.invoiceClone.id}`,{
        feeAmountToSend,
        feeType: this.type,
        saveForFuture: this.save_new_data_for_future_invoices,
      }).then(res => {
        if (res.data.status === 200) {
          this.showSuccessMessage(res.data.message);
          setTimeout(() => window.location.reload(), 1000);
        }
      })
        .catch(() => {
          this.$swal({ icon: 'error', title: this.$t('error_occurred') });
        });
    },

    CalculateNewRefund(amount, new_refund) {
      if (!this.invoiceClone) return 0;

      const discount = Number(this.invoiceClone?.scoring?.discount_from_amount) || 0;
      const base = amount - discount - (Number(new_refund) || 0);

      if (this.type === 'refund') {
        return base - (this.calculateRefundIfDefaults ? this.calculateRefundIfDefaults(this.invoiceClone) : 0);
      } else {
        return base - (CalculationsHelper.getRefundAmount(this.invoiceClone));
      }
    },

    showSuccessMessage(message) {
      this.$swal({
        position: "center",
        icon: "success",
        title: this.$t(message),
        showConfirmButton: false,
        timer: 1500,
        customClass: { confirmButton: "btn btn-primary" },
        buttonsStyling: false,
      });
    },

    closeModal() {
      this.localVisible = false;
      this.$emit('update:isVisible', false)
    },
  }
}
</script>

<style scoped>
</style>